import React, { FC, useEffect, useState } from 'react'
import { usePlacesWidget } from 'react-google-autocomplete'
import { useTranslation } from 'react-i18next'

// style components
import { MapLocation } from '../../types/mapLocation.interface'
import {
    Wrapper,
    InputWrapper,
    AutocompleteInput,
    IconCircleWrapper,
    EnterErrorMessage,
    Icon
} from './PlacesAutocomplete.style'
import useLanguage from '@hooks/useLanguage.hook'

const countriesForAutocomplete = ['de', 'at', 'ch']

interface Props {
    preselectedLocation?: MapLocation | null
    placeholder?: string
    onPlaceSelected: (mapLocation: MapLocation) => void
    onInputChange?: (value: string) => void
}

const PlacesAutocomplete: FC<Props> = ({ onPlaceSelected, placeholder, preselectedLocation, onInputChange }) => {
    const googleMapsApiKey = process.env.VITE_GOOGLE_MAPS_KEY || ''
    const [lng] = useLanguage()
    const { t } = useTranslation()

    const [showEnterMessage, setShowEnterMessage] = useState(false)

    const onAutoCompleteSelected = (place: any) => {
        if (place.name) {
            setShowEnterMessage(true)
        } else {
            setShowEnterMessage(false)
            if (place.name === '') {
                onPlaceSelected({
                    name: place.name,
                    lat: 0,
                    lng: 0,
                    address: '',
                    lotId: ''
                })
            } else {
                onPlaceSelected({
                    lat: place.geometry?.location?.lat() ?? 0,
                    lng: place.geometry?.location?.lng() ?? 0,
                    address: place.formatted_address,
                    lotId: ''
                })
            }
        }
    }

    const { ref } = usePlacesWidget({
        apiKey: googleMapsApiKey,
        language: lng,
        options: {
            componentRestrictions: { country: countriesForAutocomplete }
        },
        onPlaceSelected: (place) => {
            onAutoCompleteSelected(place)
        }
    })

    const getMyLocationData = async (latitude: number, longitude: number) => {
        try {
            const response = await fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${googleMapsApiKey}`
            )
            const data = await response.json()

            const city = data.results[0].address_components?.find(
                (component: any) =>
                    component.types.includes('postal_town') ||
                    component.types.includes('locality') ||
                    component.types.includes('administrative_area_level_3')
            )

            const country = data.results[0].address_components.find((component: any) =>
                component.types.includes('country')
            )

            const cityName = city?.long_name || t('search.unknownLocation')
            const countryName = country?.long_name || ''

            onPlaceSelected({
                lat: latitude,
                lng: longitude,
                address: `${cityName}, ${countryName}`,
                lotId: ''
            })
        } catch (error) {
            console.error('Error fetching lot data', error)
        }
    }

    const onMyLocationClick = () => {
        navigator.geolocation.getCurrentPosition(
            (position: GeolocationPosition) => {
                const { latitude, longitude } = position.coords
                getMyLocationData(latitude, longitude)
            },
            (err: GeolocationPositionError) => {
                if (err.code === err.PERMISSION_DENIED) {
                    // TODO: handle permission denied
                }
            }
        )
    }

    const onChange = (value: string) => {
        if (value && onInputChange) {
            onInputChange(value)
        }
    }

    useEffect(() => {
        if (preselectedLocation?.address === 'undefined') {
            preselectedLocation.address = undefined
        }

        if (preselectedLocation?.address && ref && ref.current && (ref.current as HTMLInputElement).value === '') {
            ;(ref.current as HTMLInputElement).value = preselectedLocation.address
        }
        if (!preselectedLocation?.address && ref && ref.current && (ref.current as HTMLInputElement).value !== '') {
            ;(ref.current as HTMLInputElement).value = ''
        }
    }, [preselectedLocation, ref])

    return (
        <Wrapper>
            <InputWrapper>
                <AutocompleteInput
                    id="places-autocomplete-input"
                    ref={ref as any}
                    onChange={(val) => {
                        onChange(val.target.value)
                    }}
                    placeholder={placeholder ? placeholder : t('common.addressSearch')}
                />
                <IconCircleWrapper onClick={onMyLocationClick}>
                    <Icon $locationIcon $clickable className="wemolo-location-2" />
                </IconCircleWrapper>
            </InputWrapper>
            {showEnterMessage && <EnterErrorMessage>{t('search.lotEnterSelect')}</EnterErrorMessage>}
        </Wrapper>
    )
}

export default PlacesAutocomplete
